import { useCallback, useEffect, useRef, useState } from 'react';
import { ENVIRONMENT } from '../config/environment';

const DEFAULT_STG_AUTH_SETTINGS = {
  enabled: ENVIRONMENT.STG_AUTH_ENABLED,
  firebaseConfig: ENVIRONMENT.FIREBASE_AUTH,
  allowedDomains: ENVIRONMENT.STG_AUTH_ALLOWED_DOMAINS,
  allowedEmails: ENVIRONMENT.STG_AUTH_ALLOWED_EMAILS,
};

const REQUIRED_FIREBASE_CONFIG_KEYS = [
  'apiKey',
  'authDomain',
  'projectId',
  'appId',
];

const getMissingFirebaseConfigKeys = config =>
  REQUIRED_FIREBASE_CONFIG_KEYS.filter(key => !config?.[key]);

// signInWithPopup がユーザー操作で閉じられた / 重複呼び出しされたケース。
// エラー扱いにせず、未認証画面に戻すだけにする。
const CANCELLED_SIGN_IN_CODES = [
  'auth/popup-closed-by-user',
  'auth/cancelled-popup-request',
  'auth/user-cancelled',
];

const isAllowedUser = (user, allowedDomains, allowedEmails) => {
  const email = user?.email?.toLowerCase();

  if (!email) {
    return false;
  }

  if (allowedEmails.includes(email)) {
    return true;
  }

  const domain = email.split('@')[1]?.toLowerCase();
  return Boolean(domain && allowedDomains.includes(domain));
};

export const useStgAuth = ({
  enabled = DEFAULT_STG_AUTH_SETTINGS.enabled,
  firebaseConfig = DEFAULT_STG_AUTH_SETTINGS.firebaseConfig,
  allowedDomains = DEFAULT_STG_AUTH_SETTINGS.allowedDomains,
  allowedEmails = DEFAULT_STG_AUTH_SETTINGS.allowedEmails,
} = DEFAULT_STG_AUTH_SETTINGS) => {
  const [state, setState] = useState({
    status: enabled ? 'loading' : 'disabled',
    user: null,
    error: null,
  });
  const authRef = useRef(null);
  const authModuleRef = useRef(null);
  const unsubscribeRef = useRef(null);
  const initializedRef = useRef(false);

  useEffect(() => {
    if (!enabled) {
      setState({ status: 'disabled', user: null, error: null });
      return undefined;
    }

    if (initializedRef.current) {
      return undefined;
    }

    let cancelled = false;
    initializedRef.current = true;
    setState({ status: 'loading', user: null, error: null });

    const initializeAuth = async () => {
      const missingConfigKeys = getMissingFirebaseConfigKeys(firebaseConfig);

      if (missingConfigKeys.length > 0) {
        if (!cancelled) {
          setState({
            status: 'error',
            user: null,
            error: `Missing Firebase Auth config: ${missingConfigKeys.join(
              ', '
            )}`,
          });
        }
        return;
      }

      try {
        const { initStgAuth } = await import('../lib/firebase');
        const { auth, authModule } = await initStgAuth(firebaseConfig);
        authRef.current = auth;
        authModuleRef.current = authModule;

        await authModule.setPersistence(
          auth,
          authModule.browserLocalPersistence
        );

        unsubscribeRef.current = authModule.onAuthStateChanged(auth, user => {
          if (cancelled) {
            return;
          }

          if (!user) {
            setState({ status: 'unauthenticated', user: null, error: null });
            return;
          }

          setState({
            status: isAllowedUser(user, allowedDomains, allowedEmails)
              ? 'authorized'
              : 'denied',
            user,
            error: null,
          });
        });
      } catch (error) {
        if (!cancelled) {
          setState({
            status: 'error',
            user: null,
            error: error instanceof Error ? error.message : String(error),
          });
        }
      }
    };

    initializeAuth();

    return () => {
      cancelled = true;
      unsubscribeRef.current?.();
      unsubscribeRef.current = null;
      initializedRef.current = false;
    };
  }, [allowedDomains, allowedEmails, enabled, firebaseConfig]);

  const signInWithGoogle = useCallback(async () => {
    const auth = authRef.current;
    const authModule = authModuleRef.current;

    if (!auth || !authModule) {
      setState({
        status: 'error',
        user: null,
        error: 'Firebase Auth is not initialized.',
      });
      return;
    }

    setState(previousState => ({
      ...previousState,
      status: 'loading',
      error: null,
    }));

    try {
      const provider = new authModule.GoogleAuthProvider();
      // onAuthStateChanged が認証成功後の status を確定させる。
      await authModule.signInWithPopup(auth, provider);
    } catch (error) {
      if (CANCELLED_SIGN_IN_CODES.includes(error?.code)) {
        setState({ status: 'unauthenticated', user: null, error: null });
        return;
      }

      setState({
        status: 'error',
        user: null,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }, []);

  const signOut = useCallback(async () => {
    const auth = authRef.current;
    const authModule = authModuleRef.current;

    if (auth && authModule) {
      await authModule.signOut(auth);
    }

    setState({ status: 'unauthenticated', user: null, error: null });
  }, []);

  return {
    ...state,
    signInWithGoogle,
    signOut,
  };
};
